import { Directive, Injector, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { CONFIG_CONTEXT, ContextNamePipe } from 'src/app/enum/config-context.enum';
import { StatisticTypeEnum } from 'src/app/enum/statistic-type.enum';
import { Project } from 'src/app/models/project';
import { ApplicationsService } from 'src/app/services/configurators-2d/administrations/applications-service';
import { ClosetDoorsStandardService } from 'src/app/services/configurators-2d/applications/closet-doors-standard.service';
import { KitchenService } from 'src/app/services/configurators-2d/applications/kitchen.service';
import { StorageService } from 'src/app/services/configurators-2d/applications/storage.service';
import { BathroomHbmService } from 'src/app/services/configurators-2d/applications/bathroom-hbm.service';
import { CUSTOMER_EVENT_NEW_PROJECT_REQUESTED, CustomerService } from 'src/app/services/customer.service';
import { CurrentService } from 'src/app/services/current.service';
import { DatasService } from 'src/app/services/datas.service';
import { getAppUrl } from 'src/app/services/helpers/app-context-helper';
import { HbmProjectOpenerService } from 'src/app/services/hbm/hbm-project-opener.service';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { Tools } from 'src/app/services/helpers/tools';
import { MODAL, ModalService } from 'src/app/services/modal.service';
import { ProjectService } from 'src/app/services/project.service';
import { StatisticService } from 'src/app/services/statistic.service';
import { environment } from 'src/environments/environment';
import { faCopy, faSearch, faTrash } from '@fortawesome/free-solid-svg-icons';
import { BasketService } from 'src/app/services/basket.service';
import { ScreenSizeDetectionService } from 'src/app/services/screen-detection.service';
import { TunnelDataStore } from 'src/app/stores/tunnel/tunnel-data.store';
import { utcDateTimestamp } from 'src/app/services/helpers/utc-date.helper';

type ContextDropdownItem = { name: string; value: CONFIG_CONTEXT | string };
export interface ContextProjectDropdownItem extends MenuItem { value: CONFIG_CONTEXT }

@Directive()
export abstract class ProjectListBaseComponent implements OnInit {
  protected loading = false;
  protected loadingProjects = false;
  protected projectsDatas: Project[] = [];
  protected displayedProjects: Project[] = [];
  protected searchInputValue = '';
  protected currentTab = 'all';
  protected multiCatalog = false;
  protected contexts = CONFIG_CONTEXT;
  protected contextDropdownItems: ContextDropdownItem[] = [];
  protected contextProjectDropdownItems: ContextProjectDropdownItem[] = [];
  protected selectedContext: CONFIG_CONTEXT;
  protected configurators2d: string[] = [];
  protected readonly projectSkeletonItems = Array.from({ length: 12 });

  /** ID du bouton splitButton "Créer un projet" dans le template */
  protected abstract get createProjectButtonId(): string;
  /** Appelé après navigation vers un projet */
  protected abstract afterNavigate(): void;
  /** Appelé après suppression ou duplication sans navigation (pour rafraîchir) */
  protected abstract afterProjectsChanged(): void;
  /** Appelé après annulation d'une suppression projet */
  protected afterProjectDeleteCancelled(): void {}
  /** Appelé après annulation d'une duplication projet */
  protected afterProjectDuplicateCancelled(): void {}

  protected readonly customer = inject(CustomerService);
  protected readonly project = inject(ProjectService);
  protected readonly current = inject(CurrentService);
  protected readonly datas = inject(DatasService);
  protected readonly applicationsService = inject(ApplicationsService);
  protected readonly closetDoorsService = inject(ClosetDoorsStandardService);
  protected readonly kitchenService = inject(KitchenService);
  protected readonly storageService = inject(StorageService);
  protected readonly bathroomHbmService = inject(BathroomHbmService);
  protected readonly contextNamePipe = inject(ContextNamePipe);
  protected readonly translate = inject(TranslateService);
  protected readonly router = inject(Router);
  protected readonly modalService = inject(ModalService);
  protected readonly injector = inject(Injector);
  private readonly hbmProjectOpener = inject(HbmProjectOpenerService);
  private readonly projectTunnelDataStore = inject(TunnelDataStore);
  private readonly basketService = inject(BasketService);
  private readonly screenDetection = inject(ScreenSizeDetectionService);

  ngOnInit(): void {
    this.initCatalogData();
    this.initConfigDropdownItems();
    this.initContextProjectDropdownItems();
    this.loadProjects();
  }

  protected initCatalogData(): void {
    let catalogCount = 0;
    if (this.datas.kitchenEnabled || this.datas.kitchenV2Enabled) catalogCount++;
    if (this.datas.storageEnabled) catalogCount++;
    if (this.datas.bathroomEnabled || this.datas.bathroomV2Enabled) catalogCount++;

    this.datas.appsEnabled.forEach(app => {
      if (app !== CONFIG_CONTEXT.KITCHEN && app !== CONFIG_CONTEXT.STORAGE && app !== CONFIG_CONTEXT.BATHROOM) {
        this.configurators2d.push(app);
        if (app !== CONFIG_CONTEXT.BATHROOM_V2) catalogCount++;
      }
    });

    this.multiCatalog = catalogCount > 1;
  }

  protected initConfigDropdownItems(): void {
    if (this.multiCatalog) {
      this.contextDropdownItems.push({ name: this.translate.instant('project-list.all-project'), value: 'all' });
    }

    if (this.datas.kitchenEnabled || this.datas.kitchenV2Enabled) {
      this.contextDropdownItems.push({ name: this.contextNamePipe.transform(CONFIG_CONTEXT.KITCHEN), value: CONFIG_CONTEXT.KITCHEN });
    }
    if (this.datas.storageEnabled) {
      this.contextDropdownItems.push({ name: this.contextNamePipe.transform(CONFIG_CONTEXT.STORAGE), value: CONFIG_CONTEXT.STORAGE });
    }
    if (this.datas.bathroomEnabled || this.datas.bathroomV2Enabled) {
      this.contextDropdownItems.push({ name: this.contextNamePipe.transform(CONFIG_CONTEXT.BATHROOM), value: CONFIG_CONTEXT.BATHROOM });
    }

    this.datas.appsEnabled.forEach(app => {
      if (app !== CONFIG_CONTEXT.KITCHEN && app !== CONFIG_CONTEXT.STORAGE && this.datas.isInDropdown(app)) {
        this.contextDropdownItems.push({ name: this.contextNamePipe.transform(app as CONFIG_CONTEXT), value: app as CONFIG_CONTEXT });
      }
    });
  }

  protected initContextProjectDropdownItems(): void {
    if (this.datas.kitchenEnabled || this.datas.kitchenV2Enabled) {
      this.contextProjectDropdownItems.push({
        label: this.contextNamePipe.transform(CONFIG_CONTEXT.KITCHEN),
        value: CONFIG_CONTEXT.KITCHEN,
        command: () => this.openConfigurator(CONFIG_CONTEXT.KITCHEN),
      });
    }
    if (this.datas.storageEnabled) {
      this.contextProjectDropdownItems.push({
        label: this.contextNamePipe.transform(CONFIG_CONTEXT.STORAGE),
        value: CONFIG_CONTEXT.STORAGE,
        command: () => this.openConfigurator(CONFIG_CONTEXT.STORAGE),
      });
    }
    if (this.datas.bathroomEnabled || this.datas.bathroomV2Enabled) {
      this.contextProjectDropdownItems.push({
        label: this.contextNamePipe.transform(CONFIG_CONTEXT.BATHROOM),
        value: CONFIG_CONTEXT.BATHROOM,
        command: () => this.openConfigurator(CONFIG_CONTEXT.BATHROOM),
      });
    }

    this.datas.topLevelAppsEnabled.forEach(context => {
      if (this.datas.isInDropdown(context)) {
        const app = environment.configurators2d.applications[context];
        this.contextProjectDropdownItems.push({
          label: this.translate.instant(`homepage.${app.distribID}.title`),
          value: app.distribID,
          command: () => this.openConfigurator(app.distribID),
        });
      }
    });

    const order = [
      CONFIG_CONTEXT.KITCHEN, CONFIG_CONTEXT.STORAGE, CONFIG_CONTEXT.BATHROOM,
      CONFIG_CONTEXT.BATHROOM_V2, CONFIG_CONTEXT.CLOSET_DOORS, CONFIG_CONTEXT.CLOSET_DOORS_STANDARD,
      CONFIG_CONTEXT.CLOSET_DOORS_CUSTOM, CONFIG_CONTEXT.WORKBENCH, CONFIG_CONTEXT.CARPENTRY,
      CONFIG_CONTEXT.FENCE, CONFIG_CONTEXT.DECKING_BOARDS, CONFIG_CONTEXT.BASKET,
    ];
    this.contextProjectDropdownItems.sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));
  }

  protected loadProjects(): void {
    this.loading = true;
    this.loadingProjects = true;

    const userId = this.customer.userDatas?.id;
    if (!userId) {
      this.loading = false;
      this.loadingProjects = false;
      return;
    }

    const promises = [this.project.getAllFromUser(userId)];
    if (this.configurators2d.length > 0) {
      promises.push(this.applicationsService.getProjects(this.configurators2d));
      if (this.datas.isAppEnabled(this.closetDoorsService.distribId)) {
        promises.push(this.closetDoorsService.getProjectsByUser(userId));
      }
    }

    Promise.allSettled(promises).then(results => {
      this.projectsDatas = [];
      results.forEach(result => {
        if (result.status === 'fulfilled') {
          this.projectsDatas = this.projectsDatas.concat(result.value);
        } else {
          logError(result.reason);
        }
      });
      this.loadingProjects = false;
      this.filterDisplayedProjects();
      this.loading = false;
    });
  }

  protected filterDisplayedProjects(): void {
    if (this.loadingProjects) return;

    const searchValue = Tools.removeAccents(this.searchInputValue.trim().toLowerCase());

    this.displayedProjects = this.projectsDatas
      .filter((p: Project) => {
        if (searchValue !== '' && !p.search.includes(searchValue)) return false;
        if (this.isTruthyProjectFlag(p.inTunnel) && !p.tunnelId && !p.tunnelContext) return false;

        return (
          this.currentTab === 'all' ||
          (this.currentTab === CONFIG_CONTEXT.KITCHEN && p.distributionID === environment.byMe.kitchen.distribID) ||
          (this.currentTab === CONFIG_CONTEXT.STORAGE && p.distributionID === environment.byMe.storage.distribID) ||
          (this.currentTab === CONFIG_CONTEXT.BATHROOM && p.distributionID === environment.byMe.bathroom.distribID) ||
          (this.currentTab === CONFIG_CONTEXT.BATHROOM_V2 && p.distributionID === environment.byMe.bathroom.distribID) ||
          this.currentTab === p.distributionID
        );
      })
      .sort((p1, p2) => utcDateTimestamp(p2.dateUpdate) - utcDateTimestamp(p1.dateUpdate));
  }

  protected setTab(tab: string): void {
    this.currentTab = tab;
    this.filterDisplayedProjects();
  }

  protected setTabFromDropdown(tab: DropdownChangeEvent): void {
    this.setTab(tab.value.value);
  }

  protected async openProject(project: Project, event: Event): Promise<void> {
    event.stopPropagation();
    if (this.isTruthyProjectFlag(project.inTunnel)) {
      await this.openTunnelProject(project);
    } else if (project.distributionID === environment.byMe.bathroom.distribID) {
      // SDB 3D (ByMe) : le projet ne porte pas de flag tunnel côté HomeByMe, on le route
      // explicitement vers le tunnel en réhydratant la configuration persistée.
      await this.openHbmTunnelProject(project, CONFIG_CONTEXT.BATHROOM);
    } else if (project.distributionID === CONFIG_CONTEXT.BATHROOM_V2) {
      this.customer.context = CONFIG_CONTEXT.BATHROOM_V2;
      this.router.navigate(['tunnel', this.getProjectConfigurationId(project)]);
      this.afterNavigate();
    } else if (project.distributionID === CONFIG_CONTEXT.CARPENTRY) {
      this.customer.context = CONFIG_CONTEXT.CARPENTRY;
      this.router.navigate(['tunnel', this.getProjectConfigurationId(project)]);
      this.afterNavigate();
    } else if (project.distributionID === CONFIG_CONTEXT.BASKET) {
      this.openBasketProject(project);
    } else {
      this.navigateToProject(project);
    }
  }

  /** Ouvre une liste d'achat dans le tunnel panier, quelle que soit la plateforme d'origine. */
  private openBasketProject(project: Project): void {
    this.customer.context = CONFIG_CONTEXT.BASKET;
    this.router.navigate(['tunnel', this.getProjectConfigurationId(project)]);
    this.afterNavigate();
  }

  private async openTunnelProject(project: Project): Promise<void> {
    switch (project.tunnelContext) {
      case CONFIG_CONTEXT.KITCHEN:
      case CONFIG_CONTEXT.STORAGE:
      case CONFIG_CONTEXT.BATHROOM:
        await this.openHbmTunnelProject(project, project.tunnelContext as CONFIG_CONTEXT);
        break;
      case CONFIG_CONTEXT.BATHROOM_V2:
      case CONFIG_CONTEXT.CARPENTRY:
        this.customer.context = project.tunnelContext;
        this.router.navigate(['tunnel', this.getProjectConfigurationId(project)]);
        this.afterNavigate();
        break;
      default:
        this.navigateToProject(project);
        break;
    }
  }

  private async openHbmTunnelProject(project: Project, context: CONFIG_CONTEXT): Promise<void> {
    this.loading = true;
    try {
      await this.hbmProjectOpener.open(context, this.getHbmProjectId(project), project.extraDatas?.isLegacy);
      this.afterNavigate();
    } catch (error) {
      logError(error);
    } finally {
      this.loading = false;
    }
  }

  protected navigateToProject(project: Project): void {
    if (this.configurators2d.includes(project.distributionID)) {
      if (project.distributionID === CONFIG_CONTEXT.BATHROOM_V2) {
        this.customer.context = CONFIG_CONTEXT.BATHROOM_V2;
        this.router.navigate(['tunnel', this.getProjectConfigurationId(project)]);
      } else if (project.distributionID === CONFIG_CONTEXT.CARPENTRY) {
        this.customer.context = CONFIG_CONTEXT.CARPENTRY;
        this.router.navigate(['tunnel', this.getProjectConfigurationId(project)]);
      } else if (project.distributionID === CONFIG_CONTEXT.BASKET) {
        this.customer.context = CONFIG_CONTEXT.BASKET;
        this.router.navigate(['tunnel', this.getProjectConfigurationId(project)]);
      } else {
        this.router.navigate([getAppUrl(project.distributionID), 'open', project.projectUUID]);
      }
    } else {
      this.router.navigate(['projects', project.projectUUID]);
    }
    this.afterNavigate();
  }

  protected duplicateProject(project: Project, event: Event): void {
    event.stopPropagation();
    this.modalService.display(MODAL.PROJECT_DUPLICATE, {
      project,
      onClose: (clonedProject: Project) => {
        if (clonedProject) {
          this.navigateToProject(clonedProject);
        } else {
          this.afterProjectDuplicateCancelled();
        }
      },
    });
  }

  protected deleteProject(project: Project, event: Event): void {
    event.stopPropagation();
    this.modalService.display(MODAL.PROJECT_DELETE, {
      project,
      onClose: (projectDeleted: boolean) => {
        if (projectDeleted) {
          this.afterProjectsChanged();
        } else {
          this.afterProjectDeleteCancelled();
        }
      },
    });
  }

  protected isProjectDeleteDisabled(project: Project): boolean {
    return this.isTruthyProjectFlag(project.inTunnel) || this.isTruthyProjectFlag(project.is_locked);
  }

  private isTruthyProjectFlag(value: unknown): boolean {
    return value === true || value === 'true' || value === 1;
  }

  private getProjectConfigurationId(project: Project): string {
    return project.projectID ?? project.projectUUID;
  }

  private getHbmProjectId(project: Project): string {
    return project.tunnelId ?? project.projectUUID;
  }

  protected openConfigurator(context: CONFIG_CONTEXT): void {
    this.prepareNewProject(context);

    if (context === CONFIG_CONTEXT.BATHROOM || context === CONFIG_CONTEXT.BATHROOM_V2) {
      this.router.navigate(['salle-de-bain']);
      this.afterNavigate();
      return;
    }

    this.customer.events.emit({
      type: CUSTOMER_EVENT_NEW_PROJECT_REQUESTED,
      context,
    });
    this.router.navigate(['tunnel']);
    this.afterNavigate();
  }

  private prepareNewProject(context: CONFIG_CONTEXT): void {
    this.projectTunnelDataStore.resetState(false);

    if (context === CONFIG_CONTEXT.BASKET) {
      this.basketService.startNewPurchaseList();
    }

    this.customer.context = context;
    this.sendNewProjectCountStatistic(context);
  }

  protected openDropdown(): void {
    setTimeout(() => {
      const menuButton = document.querySelector(`#${this.createProjectButtonId} .p-splitbutton-menubutton`);
      const overlayVisible = document.querySelector(`#${this.createProjectButtonId} .p-tieredmenu-overlay`);
      if (menuButton instanceof HTMLElement) {
        overlayVisible ? document.body.click() : menuButton.click();
      }
    }, 0);
  }

  protected sendNewProjectCountStatistic(context: string): void {
    let statType = StatisticTypeEnum.NEW_PROJECT_CLICKED_COUNT;
    if (context === CONFIG_CONTEXT.BASKET) {
      statType = this.screenDetection.isMobile()
        ? StatisticTypeEnum.BASKET_NEW_PROJECT_CLICKED_COUNT_MOBILE
        : StatisticTypeEnum.BASKET_NEW_PROJECT_CLICKED_COUNT_DESKTOP;
    }
    this.injector.get(StatisticService).sendStatistic(statType, 1, context);
  }

  protected readonly faSearch = faSearch;
  protected readonly faTrash = faTrash;
  protected readonly faCopy = faCopy;
}
